import { useRef, useState } from 'preact/hooks';
import { SendIcon, PaperclipIcon, CloseIcon } from './Icons.jsx';
import { LIMITS } from '@superchat/shared/constants';
import { formatBytes } from '../upload.js';

let uploadSeq = 0;

/**
 * Enter sends, Shift+Enter breaks the line — the convention every merchant
 * already has in their fingers from every other chat tool.
 *
 * Files upload the moment they are picked, not when Send is pressed: by the
 * time someone has finished typing "here's the screenshot", the screenshot is
 * already in storage and the send is instant.
 */
export function Composer({ onSend, onTyping, sending, onUpload, uploads }) {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState([]);
  const box = useRef(null);
  const picker = useRef(null);
  const typing = useRef(false);

  const grow = () => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  };

  const signalTyping = (isTyping) => {
    if (typing.current === isTyping) return;
    typing.current = isTyping;
    onTyping(isTyping);
  };

  const attach = (picked) => {
    const list = Array.from(picked ?? []).filter(Boolean);
    if (!list.length || !uploads?.enabled) return;

    for (const file of list) {
      const id = `u_${(uploadSeq += 1)}`;

      if (uploads.maxBytes && file.size > uploads.maxBytes) {
        setFiles((current) => [
          ...current,
          { id, name: file.name, size: file.size, error: `Max ${formatBytes(uploads.maxBytes)}` },
        ]);
        continue;
      }

      setFiles((current) => [...current, { id, name: file.name, size: file.size, progress: 0 }]);

      onUpload(file, {
        onProgress: (fraction) =>
          setFiles((current) =>
            current.map((f) => (f.id === id ? { ...f, progress: fraction } : f))
          ),
      })
        .then((uploaded) =>
          setFiles((current) =>
            current.map((f) => (f.id === id ? { ...f, ...uploaded, progress: 1 } : f))
          )
        )
        .catch(() =>
          setFiles((current) =>
            current.map((f) =>
              f.id === id ? { ...f, error: 'Upload failed', progress: undefined } : f
            )
          )
        );
    }
  };

  const ready = files.filter((f) => f.key);
  const uploading = files.some((f) => !f.key && !f.error);

  const submit = () => {
    const body = value.trim();
    if (sending || uploading) return;
    if (!body && ready.length === 0) return;

    onSend({
      body,
      attachments: ready.map(({ key, name, mimeType, size }) => ({ key, name, mimeType, size })),
    });
    setValue('');
    setFiles([]);
    signalTyping(false);
    requestAnimationFrame(grow);
  };

  return (
    <div class="composer-wrap">
      {files.length > 0 && (
        <div class="composer__files">
          {files.map((file) => (
            <span
              key={file.id}
              class={`chip${file.error ? ' chip--failed' : ''}${file.key ? ' chip--ready' : ''}`}
              title={file.error || file.name}
            >
              <span class="chip__name">{file.name}</span>
              <span class="chip__meta">
                {file.error
                  ? file.error
                  : file.key
                    ? formatBytes(file.size)
                    : `${Math.round((file.progress ?? 0) * 100)}%`}
              </span>
              <button
                class="chip__remove"
                type="button"
                onClick={() => setFiles((current) => current.filter((f) => f.id !== file.id))}
                aria-label={`Remove ${file.name}`}
              >
                <CloseIcon size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div
        class="composer"
        onDragOver={(e) => uploads?.enabled && e.preventDefault()}
        onDrop={(e) => {
          if (!uploads?.enabled) return;
          e.preventDefault();
          attach(e.dataTransfer?.files);
        }}
      >
        <textarea
          ref={box}
          class="composer__input"
          rows={1}
          maxLength={LIMITS.MESSAGE_BODY_MAX}
          placeholder="Write a reply…"
          aria-label="Write a reply"
          value={value}
          onInput={(e) => {
            setValue(e.currentTarget.value);
            signalTyping(e.currentTarget.value.length > 0);
            grow();
          }}
          onBlur={() => signalTyping(false)}
          onPaste={(e) => {
            // A pasted screenshot is the single most common attachment here.
            const pasted = Array.from(e.clipboardData?.files ?? []);
            if (!pasted.length) return;
            e.preventDefault();
            attach(pasted);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />

        {uploads?.enabled && (
          <>
            <input
              ref={picker}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                attach(e.currentTarget.files);
                e.currentTarget.value = '';
              }}
            />
            <button
              class="composer__icon"
              type="button"
              onClick={() => picker.current?.click()}
              title={`Attach a file — up to ${formatBytes(uploads.maxBytes)}`}
              aria-label="Attach a file"
            >
              <PaperclipIcon size={16} />
            </button>
          </>
        )}

        <button
          class="composer__send"
          type="button"
          onClick={submit}
          disabled={sending || uploading || (!value.trim() && ready.length === 0)}
          aria-label="Send message"
        >
          <SendIcon size={14} />
        </button>
      </div>
    </div>
  );
}

export default Composer;
